import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export type SavedKind = 'report' | 'view' | 'filter_set' | 'dashboard';

export interface SavedItem<T = Record<string, unknown>> {
  id: string;
  kind: SavedKind;
  screen: string | null;
  name: string;
  description: string | null;
  definition: T;
  isShared: boolean;
  isDefaultForScreen: boolean;
  pinned: boolean;
  pinPosition: number | null;
  isOwner: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SavedInput {
  kind: SavedKind;
  screen?: string | null;
  name: string;
  description?: string | null;
  definition: Record<string, unknown>;
  isShared?: boolean;
  isDefaultForScreen?: boolean;
}

export function useSavedItems(
  filter: { kind?: SavedKind; screen?: string; pinned?: boolean } = {},
  enabled = true,
) {
  const params = new URLSearchParams();
  if (filter.kind) params.set('kind', filter.kind);
  if (filter.screen) params.set('screen', filter.screen);
  if (filter.pinned) params.set('pinned', 'true');
  const qs = params.toString();
  return useQuery({
    queryKey: ['saved', filter.kind ?? 'all', filter.screen ?? 'all', filter.pinned ?? false],
    queryFn: () => api<{ items: SavedItem[] }>(`/api/saved${qs ? `?${qs}` : ''}`),
    enabled,
    staleTime: 60_000,
    meta: { persist: true },
  });
}

export function useSavedItem(id: string | null) {
  return useQuery({
    queryKey: ['saved-item', id],
    queryFn: () => api<SavedItem>(`/api/saved/${id}`),
    enabled: Boolean(id),
    staleTime: 60_000,
    meta: { persist: true },
  });
}

export function useSavedMutations() {
  const client = useQueryClient();
  const done = () => {
    void client.invalidateQueries({ queryKey: ['saved'] });
    void client.invalidateQueries({ queryKey: ['saved-item'] });
  };
  return {
    create: useMutation({
      mutationFn: (input: SavedInput) => api<{ id: string }>('/api/saved', { method: 'POST', body: input }),
      onSuccess: done,
    }),
    update: useMutation({
      mutationFn: ({ id, ...patch }: Partial<SavedInput> & { id: string }) =>
        api(`/api/saved/${id}`, { method: 'PATCH', body: patch }),
      onSuccess: done,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api(`/api/saved/${id}`, { method: 'DELETE' }),
      onSuccess: done,
    }),
    pin: useMutation({
      mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) =>
        api(`/api/saved/${id}/pin`, { method: 'POST', body: { pinned } }),
      onSuccess: done,
    }),
  };
}
